import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import './ManageProjectUsersModal.css';
import { baseApi } from '../../../../API/api';

// Material Symbols font is loaded globally in index.html with all needed icons (accessibility_new, delete, explore)
// No need to dynamically load it here

const ROLE_OPTIONS = [
  { value: 1, label: 'Administrator' },
  { value: 2, label: 'Editor' },
  { value: 3, label: 'Viewer' }
];

const defaultRoleValue = ROLE_OPTIONS[0].value;

const ManageProjectUsersModal = ({ projectId, projectName = '', isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState(defaultRoleValue);
  const [formMessage, setFormMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [feedbackExiting, setFeedbackExiting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);

  const modalTitle = useMemo(() => {
    if (!projectName) {
      return 'Manage project team';
    }
    return `Manage project team • ${projectName}`;
  }, [projectName]);

  useEffect(() => {
    if (isOpen) {
      fetchProjectUsers();
    } else {
      setFormEmail('');
      setFormMessage('');
      setFormSuccess('');
      setFormError('');
      setFeedbackExiting(false);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId]);

  useEffect(() => {
    if (!formError && !formSuccess) {
      setFeedbackExiting(false);
      return;
    }
    const exitStart = setTimeout(() => setFeedbackExiting(true), 4700);
    const clear = setTimeout(() => {
      setFormError('');
      setFormSuccess('');
      setFeedbackExiting(false);
    }, 5000);
    return () => {
      clearTimeout(exitStart);
      clearTimeout(clear);
    };
  }, [formError, formSuccess]);

  const fetchProjectUsers = async () => {
    if (!projectId) {
      return;
    }
    setLoading(true);
    setFormError('');

    try {
      const response = await axios.get(`${baseApi}/api/projects/${projectId}/users`);
      const fetchedUsers = response.data?.users || [];
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Failed to load project users:', error);
      const backendMessage = error.response?.data?.error;
      setFormError(backendMessage || 'Failed to load project users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const trimmed = email.trim();
    if (!trimmed) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleAddUser = async (event) => {
    event.preventDefault();
    setFormError('');
    setFormSuccess('');

    const validationError = validateEmail(formEmail);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${baseApi}/api/projects/${projectId}/users`, {
        email: formEmail.trim(),
        role_id: formRole,
        message: formMessage
      });

      setFormEmail('');
      setFormMessage('');
      setFormSuccess('User added to the project successfully.');

      await fetchProjectUsers();
    } catch (error) {
      console.error('Failed to add user to project:', error);
      const backendResponse = error.response?.data;
      if (backendResponse?.code === 'USER_ALREADY_ASSIGNED') {
        setFormError('This user is already part of the project.');
      } else if (backendResponse?.code === 'USER_NOT_FOUND') {
        setFormError('We could not find a user with that email address.');
      } else {
        const backendMessage = backendResponse?.error || error.message;
        setFormError(backendMessage || 'Unable to add user right now.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (event) => {
    if (event.target.classList.contains('manage-project-users-overlay')) {
      onClose?.();
    }
  };

  const handleRemoveUser = async (userId) => {
    setFormError('');
    setFormSuccess('');
    setRemovingUserId(userId);
    try {
      await axios.delete(`${baseApi}/api/projects/${projectId}/users/${userId}`);
      setFormSuccess('User removed from the project.');
      await fetchProjectUsers();
    } catch (error) {
      console.error('Failed to remove user from project:', error);
      const backendMessage = error.response?.data?.error || error.message;
      setFormError(backendMessage || 'Unable to remove user right now.');
    } finally {
      setRemovingUserId(null);
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalMarkup = (
    <div
      className="manage-project-users-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className="manage-project-users-modal" onClick={(event) => event.stopPropagation()}>
        <header className="manage-project-users-header">
          <div>
            <h2>{modalTitle}</h2>
            <p className="manage-project-users-subtitle">
              Add or remove team members who can access this project.
            </p>
          </div>
          <button
            type="button"
            className="manage-project-users-close"
            onClick={onClose}
            aria-label="Close manage project users modal"
          >
            ×
          </button>
        </header>

        <section className="manage-project-users-form-section manage-project-users-column">
          <form className="manage-project-users-form" onSubmit={handleAddUser}>
            <div className="manage-project-users-field">
              <label htmlFor="manage-project-users-email">Email</label>
              <input
                id="manage-project-users-email"
                type="email"
                className="manage-project-users-email-input"
                placeholder="user@example.com"
                value={formEmail}
                onChange={(event) => setFormEmail(event.target.value)}
                disabled={submitting}
              />
            </div>

            <div className="manage-project-users-field half-width">
              <label htmlFor="manage-project-users-role">Role</label>
              <select
                id="manage-project-users-role"
                value={formRole}
                onChange={(event) => setFormRole(Number(event.target.value))}
                disabled={submitting}
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="manage-project-users-field">
              <label htmlFor="manage-project-users-message">Your message</label>
              <textarea
                id="manage-project-users-message"
                placeholder="Insert your message here (optional)"
                value={formMessage}
                onChange={(event) => setFormMessage(event.target.value)}
                disabled={submitting}
              />
            </div>

            <div className="manage-project-users-actions">
              <button
                type="submit"
                className="manage-project-users-add"
                disabled={submitting}
              >
                <i className="fas fa-plus"></i>
                {submitting ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>

          <div
            className={`manage-project-users-feedback ${formError || formSuccess ? 'manage-project-users-feedback-visible' : ''} ${feedbackExiting ? 'manage-project-users-feedback-exiting' : ''}`}
          >
            {formError && <div className="manage-project-users-error">{formError}</div>}
            {formSuccess && <div className="manage-project-users-success">{formSuccess}</div>}
          </div>
        </section>

        <section className="manage-project-users-table-section manage-project-users-column">
          <div className="manage-project-users-table-wrapper">
            {loading ? (
              <div className="manage-project-users-loading">Loading users…</div>
            ) : users.length === 0 ? (
              <div className="manage-project-users-empty">
                No users have access to this project yet.
              </div>
            ) : (
              <table className="manage-project-users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const rowKey = user.composite_key || `${projectId}_${user.user_id}`;
                    return (
                      <tr key={rowKey}>
                        <td>
                          <div className="manage-project-users-email">
                            {user.email}
                          </div>
                        </td>
                        <td>
                          <span className="manage-project-users-role-tag">
                            {user.role_label || ROLE_OPTIONS.find((r) => r.value === user.role_id)?.label || `Role ${user.role_id}`}
                          </span>
                        </td>
                    <td>
                      <button
                        type="button"
                        className="manage-project-users-action-btn"
                        onClick={() => handleRemoveUser(user.user_id)}
                        disabled={removingUserId === user.user_id}
                        title="Remove user from project"
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          delete
                        </span>
                      </button>
                    </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalMarkup;
  }

  return createPortal(modalMarkup, document.body);
};

export default ManageProjectUsersModal;